import * as VOXLib from 'voximplant-websdk';
import type { Call } from 'voximplant-websdk/Call/Call';
import type { MediaRenderer } from 'voximplant-websdk/Media/MediaRenderer';

import type { IUser } from 'store/declare';
import { userAdapter } from 'store/users/helpers/userAdapter';

import { screenShareManager } from 'manager/screenShareManager';
import { videoManager } from 'manager/videoManager';

import { Provider } from 'providers';

interface EndpointUpdateEventData {
    endpoint: VOXLib.Endpoint;
}

interface Parameters {
    currentUserId?: string;
    updateSize: React.Dispatch<React.SetStateAction<boolean>>;
    updateUserList: React.Dispatch<React.SetStateAction<IUser[]>>;
    voxCall: Call;
}

/** получить ID пользователя в наших сервисах */
function getEndpointId(endpoint: VOXLib.Endpoint) {
    return endpoint.userName;
}

// коллекция пользователей, информация о которых получена с сервера
const userWithDataSet = new Set<string>();
// коллекция пользователей, информация о которых будет запрошена с сервера
const userDataWillBeRequestedSet = new Set<string>();
// список функций, которые должны выполниться после добавления пользователя в список участников
const endpointAddedActionQueue = new Map<string, (() => void)[]>();

/** Установить слушатель события добавления участников в конференцию */
export function attachVOXEndpointUpdateEvent({ currentUserId, updateSize, updateUserList, voxCall }: Parameters) {
    /** Удалить пользователя из списка пользователей */
    function removeUserFromList(endpoint: VOXLib.Endpoint) {
        const endpointId = getEndpointId(endpoint);

        updateUserList(prevState => prevState.filter(user => user.id !== endpointId));
        userWithDataSet.delete(endpointId);
        userDataWillBeRequestedSet.delete(endpointId);
    }

    /** Пользователь вошел в конференцию или пользователь начал демонстрацию экрана */
    voxCall.on(VOXLib.CallEvents.EndpointAdded, async ({ endpoint }: EndpointUpdateEventData) => {
        // endpoint - любая единица, которая присоединяется к конференции
        // когда пользователь шарит экран - от его имени к конференции присоединяется еще один участник (endpoint), который демонстрирует экран.
        // Аналогично сделано в слаке.
        // В момент добавления участника в конференцию нет возможности определить, является ли endpoint настоящим пользователем,
        // или пользователем, созданным программно при помощи демонстрации экрана.

        const endpointId = getEndpointId(endpoint);
        // endpoint является текущим пользователем
        const isCurrentUser = currentUserId === endpointId;
        // endpoint является НЕ текущим пользователем
        const isNotCurrentUser = currentUserId !== endpointId;

        // при первом подключении любого endpoint от пользователя - запрашиваем информацию по пользователю
        // и добавляем его в список пользователей
        if (isNotCurrentUser) {
            if (!userDataWillBeRequestedSet.has(endpointId) && !userWithDataSet.has(endpointId)) {
                userDataWillBeRequestedSet.add(endpointId);
                Provider.getUserData(endpointId).then(userData => {
                    const user = userAdapter(userData);

                    updateUserList(prevState => [...prevState.filter(prevStateUser => prevStateUser.id !== user.id), user]);
                    userWithDataSet.add(endpointId);
                    userDataWillBeRequestedSet.delete(endpointId);
                });
            }
        }

        /** Обновление состояния собеседника */
        function toggleUserBooleanState({
            property,
            state,
        }: {
            property: Extract<keyof IUser, 'isTalking' | 'isSharing' | 'isCameraEnabled'>;
            state: boolean;
        }) {
            updateUserList(prevState => {
                return prevState.map(user => {
                    if (user.id === endpointId) user[property] = state;

                    return user;
                });
            });
        }

        /** Собеседник начал говорить в микрофон */
        endpoint.on(VOXLib.EndpointEvents.VoiceStart, () => toggleUserBooleanState({ property: 'isTalking', state: true }));

        /** Собеседник прекратил говорить в микрофон */
        endpoint.on(VOXLib.EndpointEvents.VoiceEnd, () => toggleUserBooleanState({ property: 'isTalking', state: false }));

        endpoint.on(VOXLib.EndpointEvents.RemoteMediaAdded, async ({ mediaRenderer }: { mediaRenderer: MediaRenderer }) => {
            // событие RemoteMediaAdded срабатывает несколько раз для каждого endpoint (audio / video / sharing)
            // на момент срабатывания события информация о пользователе может быть еще не доступна
            // для того чтобы отобразить изменения в UI необходимо добавить пользователя в список участников
            // по интервалу проверяем наличие пользователя в списке участников
            // если такой пользователь появился - вызываем все сохраненные действия для этого пользователя
            if (!endpointAddedActionQueue.has(endpointId)) endpointAddedActionQueue.set(endpointId, []);

            endpointAddedActionQueue.get(endpointId)?.push(() => {
                if (mediaRenderer.kind === 'sharing') {
                    // переключение состояния "Демонстрирует экран" у собеседника
                    toggleUserBooleanState({ property: 'isSharing', state: true });
                    // создаем видео контент демонстрации экрана
                    screenShareManager.set({ mediaRenderer, userId: endpointId })?.draw();
                }

                // создаем видео контент собеседника
                if (mediaRenderer.kind === 'video') {
                    // добавляем пользователя в список, для дальнейшей отрисовки контента с камеры
                    videoManager.set({ mediaRenderer, userId: endpointId });

                    toggleUserBooleanState({ property: 'isCameraEnabled', state: true });
                }
            });

            const timer = window.setInterval(() => {
                if (userWithDataSet.has(endpointId) || isCurrentUser) {
                    endpointAddedActionQueue.get(endpointId)?.forEach(action => action());
                    endpointAddedActionQueue.delete(endpointId);
                    window.clearInterval(timer);
                }
            }, 500);
        });

        endpoint.on(VOXLib.EndpointEvents.RemoteMediaRemoved, ({ mediaRenderer }: { mediaRenderer: MediaRenderer }) => {
            if (mediaRenderer.kind === 'sharing') {
                // удаляем видео контент демонстрации экрана
                screenShareManager.remove(endpointId);
                // выключаем режим "Во весь экран" если собеседник прекратил демонстрацию
                updateSize(false);
                // переключение состояния "Демонстрирует экран" у собеседника
                toggleUserBooleanState({ property: 'isSharing', state: false });
            }

            // удаляем видео контент собеседника
            if (mediaRenderer.kind === 'video') {
                videoManager.getVideoManagerByUserId(endpointId)?.remove();
                toggleUserBooleanState({ property: 'isCameraEnabled', state: false });
            }
        });
    });
    /** Пользователь вышел из конференции / Пользователь прекратил демонстрацию экрана */
    voxCall.on(VOXLib.CallEvents.EndpointRemoved, ({ endpoint }: EndpointUpdateEventData) => {
        // удаляем собеседника из списка только если собеседник действительно вышел
        // из конференции
        const isSharingOffEvent = endpoint.mediaRenderers.some(renderer => renderer.kind === 'sharing');

        if (!isSharingOffEvent) {
            removeUserFromList(endpoint);
            videoManager.remove(getEndpointId(endpoint));
        }
    });
}
